'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CalendarGlyph, CloseGlyph } from '../../../internal/icons.js';
import { addDays, dateFormatHint, formatFullDate, formatIsoDate, formatNumericDate, parseDateInput, useDateText, useToday, validateIsoDate, } from '../../../internal/dates/index.js';
import { useFieldControl } from '../../forms/form-field/index.js';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger, } from '../../overlays/popover/index.js';
import { MiniMonth } from '../mini-month/index.js';
const DEFAULT_MESSAGES = {
    invalid: (hint) => `Enter a date as ${hint}.`,
    beforeMin: (min) => `Choose ${min} or later.`,
    afterMax: (max) => `Choose ${max} or earlier.`,
    unavailable: 'That date is not available.',
    openCalendar: 'Choose date',
    changeDate: (date) => `Change date, ${date}`,
    clear: 'Clear date',
    dialogLabel: 'Choose date',
    presets: 'Quick picks',
};
/** Focuses the roving Tab stop of the first calendar grid inside `container`. */
function focusCurrentDay(container) {
    if (container instanceof HTMLElement)
        container.querySelector('[data-date][tabindex="0"]')?.focus();
}
/**
 * A date field: type a date in the locale's format, or pick it from a
 * calendar popover with optional quick picks. The value is an ISO
 * `YYYY-MM-DD` string with no time or time zone. Validates min, max and
 * disabled dates, and reports errors through Field.
 */
export const DatePicker = forwardRef(function DatePicker({ value: valueProp, defaultValue = null, onValueChange, min, max, isDateDisabled, presets, clearable = true, size = 'md', invalid, locale, formatOptions, weekStartsOn, showWeekNumbers, getDayInfo, today: todayProp, open: openProp, onOpenChange, onValidationChange, showErrors = true, messages: messagesProp, className, name, placeholder, readOnly, onBlur, onKeyDown, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const today = useToday(todayProp);
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const errorId = `${useId()}-error`;
    const [inner, setInner] = useState(defaultValue);
    const value = valueProp !== undefined ? valueProp : inner;
    const commitValue = (next) => {
        if (valueProp === undefined)
            setInner(next);
        onValueChange?.(next);
    };
    const [innerOpen, setInnerOpen] = useState(false);
    const open = openProp ?? innerOpen;
    const setOpen = (next) => {
        if (openProp === undefined)
            setInnerOpen(next);
        onOpenChange?.(next);
    };
    const hint = dateFormatHint(locale);
    const format = (iso) => formatOptions ? formatIsoDate(iso, locale, formatOptions) : formatNumericDate(iso, locale);
    const constraints = { min, max, isDateDisabled };
    const field = useDateText({
        value,
        format,
        parse: (t) => parseDateInput(t, { locale, referenceDate: today }),
        validate: (iso) => validateIsoDate(iso, constraints),
        onCommit: commitValue,
    });
    const reported = useRef(null);
    const validationRef = useRef(onValidationChange);
    validationRef.current = onValidationChange;
    useEffect(() => {
        if (reported.current === field.error)
            return;
        reported.current = field.error;
        validationRef.current?.(field.error);
    }, [field.error]);
    const short = (iso) => formatIsoDate(iso, locale, { dateStyle: 'medium' });
    const message = field.error === 'invalid'
        ? messages.invalid(hint)
        : field.error === 'before-min' && min
            ? messages.beforeMin(short(min))
            : field.error === 'after-max' && max
                ? messages.afterMax(short(max))
                : field.error
                    ? messages.unavailable
                    : null;
    const showError = showErrors && !!message;
    const control = useFieldControl({
        ...rest,
        'aria-describedby': cx(rest['aria-describedby'], showError && errorId),
    }, { invalid: invalid || !!field.error || undefined });
    const disabled = !!control.disabled;
    const choose = (iso) => {
        field.set(iso);
        setOpen(false);
    };
    const handleKeyDown = (event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented)
            return;
        if (event.key === 'Enter')
            field.commit();
        else if (event.key === 'ArrowDown' && !readOnly) {
            event.preventDefault();
            field.commit();
            setOpen(true);
        }
    };
    const resolvedPresets = (presets ?? []).map((p) => {
        const date = p.date ?? addDays(today, p.days ?? 0);
        return { label: p.label, date, disabled: !!validateIsoDate(date, constraints) };
    });
    return (_jsxs("div", { className: cx('mn-date-picker', className), "data-size": size, "data-disabled": disabled || undefined, "data-invalid": control['aria-invalid'] ? '' : undefined, children: [_jsxs(Popover, { open: open && !disabled && !readOnly, onOpenChange: (next) => {
                    if (next)
                        field.commit();
                    setOpen(next);
                }, modal: true, children: [_jsx(PopoverAnchor, { asChild: true, children: _jsxs("div", { className: "mn-input mn-date-picker__box", "data-size": size, "data-disabled": disabled || undefined, children: [_jsx("input", { ref: inputRef, ...control, type: "text", autoComplete: "off", spellCheck: false, readOnly: readOnly, placeholder: placeholder ?? hint, className: "mn-date-picker__input", value: field.text, onChange: (e) => field.onTextChange(e.target.value), onBlur: (e) => {
                                        onBlur?.(e);
                                        field.commit();
                                    }, onKeyDown: handleKeyDown }), clearable && value && !disabled && !readOnly ? (_jsx("button", { type: "button", className: "mn-date-picker__clear", "aria-label": messages.clear, onClick: () => {
                                        field.set(null);
                                        inputRef.current?.focus();
                                    }, children: _jsx(CloseGlyph, {}) })) : null, _jsx(PopoverTrigger, { asChild: true, children: _jsx("button", { type: "button", className: "mn-date-picker__trigger", "aria-label": value ? messages.changeDate(formatFullDate(value, locale)) : messages.openCalendar, disabled: disabled || readOnly, children: _jsx(CalendarGlyph, {}) }) })] }) }), _jsx(PopoverContent, { className: "mn-date-picker__popover", "aria-label": messages.dialogLabel, onOpenAutoFocus: (e) => {
                            // Focus the calendar's current day rather than the first button.
                            e.preventDefault();
                            focusCurrentDay(e.currentTarget);
                        }, onCloseAutoFocus: (e) => {
                            e.preventDefault();
                            inputRef.current?.focus();
                        }, children: _jsxs("div", { className: "mn-date-picker__panel", "data-presets": resolvedPresets.length ? '' : undefined, children: [resolvedPresets.length ? (_jsx("div", { role: "group", "aria-label": messages.presets, className: "mn-date-picker__presets", children: resolvedPresets.map((p) => (_jsxs("button", { type: "button", className: "mn-date-picker__preset", disabled: p.disabled, "aria-pressed": p.date === value, onClick: () => choose(p.date), children: [_jsx("span", { children: p.label }), _jsx("span", { className: "mn-date-picker__preset-date", children: formatIsoDate(p.date, locale, {
                                                    weekday: 'short',
                                                    day: 'numeric',
                                                    month: 'short',
                                                }) })] }, p.label))) })) : null, _jsx(MiniMonth, { value: value, onValueChange: (d) => choose(d), min: min, max: max, isDateDisabled: isDateDisabled, getDayInfo: getDayInfo, today: today, locale: locale, weekStartsOn: weekStartsOn, showWeekNumbers: showWeekNumbers })] }) })] }), name ? _jsx("input", { type: "hidden", name: name, value: value ?? '', disabled: disabled }) : null, showError ? (_jsx("p", { id: errorId, className: "mn-field__error mn-date-picker__error", "aria-live": "polite", children: message })) : null] }));
});
//# sourceMappingURL=date-picker.js.map